import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';

const NgatResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [complaintData, setComplaintData] = useState({
    category: '',
    description: '',
    phoneNumber: ''
  });
  const [complaintReference, setComplaintReference] = useState('');
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState(null);

  const complaintCategories = [
    { value: 'result_not_found', label: 'My result not found' },
    { value: 'disqualified_no_reason', label: 'My result disqualified without reason' },
    { value: 'attendance_ng_grade', label: "I've signed attendance but my grade is NG" },
    { value: 'wrong_gender', label: 'Wrong gender information' },
    { value: 'other', label: 'Other' }
  ];

  const handleComplaintSubmit = async () => {
    if (Number(result?.complaint_status ?? 0) !== 1) {
      setValidationError('Complaint period is over.');
      return;
    }

    const now = Date.now();
    if (lastSubmissionTime && (now - lastSubmissionTime) < 30000) {
      setValidationError('Please wait 30 seconds before submitting another complaint to prevent duplicates.');
      return;
    }

    if (!complaintData.category || !complaintData.description.trim() || !complaintData.phoneNumber.trim()) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    if (complaintData.phoneNumber.trim().length > 10) {
      setValidationError('Phone number must not exceed 10 characters.');
      return;
    }

    setValidationError('');
    setIsSubmittingComplaint(true);

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
      const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

      const response = await fetch(`${API_URL}/ngat-complaints`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: result.student_username,
          full_name: result.student_name || 'N/A',
          category: complaintData.category,
          description: complaintData.description.trim(),
          phoneNumber: complaintData.phoneNumber.trim(),
          exam_date: result.exam_date || '',
          exam_center_name: result.exam_center_name || '',
          speciality: result.speciality || result.specialty || ''
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setShowComplaintModal(false);
        setShowSuccessModal(true);
        setLastSubmissionTime(Date.now());
        setComplaintData({ category: '', description: '', phoneNumber: '' });
        setValidationError('');
        setComplaintReference(data.data?.reference_number || 'N/A');
      } else {
        setValidationError(data.message || 'Failed to submit complaint. Please try again.');
      }
    } catch (error) {
      console.error('Complaint submission error:', error);
      setValidationError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingComplaint(false);
    }
  };

  const handleCancelComplaint = () => {
    setShowComplaintModal(false);
    setComplaintData({ category: '', description: '', phoneNumber: '' });
    setValidationError('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setComplaintReference('');
  };

  const copyReferenceNumber = async () => {
    try {
      await navigator.clipboard.writeText(complaintReference);
      setCopyConfirmed(true);
      setTimeout(() => setCopyConfirmed(false), 2000); // Hide confirmation after 2 seconds
    } catch (err) {
      console.error('Failed to copy reference number:', err);
      alert('Failed to copy reference number. Please copy it manually.');
    }
  };

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setLoading(true);
        setError(null);

        const resultData = location.state?.resultData;
        if (!resultData) {
          setError('No result data found');
          return;
        }

        setResult(resultData);
      } catch (err) {
        console.error('Error fetching result:', err);
        setError('Failed to load result data');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [location.state]);

  const downloadCertificate = async () => {
    if (!result) {
      console.error('No result data available');
      return;
    }

    setIsDownloading(true);

    try {
          const response = await fetch('https://eap.ethernet.edu.et/api/ngat-certificate', {
            method: 'POST',
            headers: {
              'X-API-Key': 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_token: result.user_token
            })
          });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the PDF blob from the response
      const pdfBlob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
          link.download = `NGAT_Certificate_${result.student_username}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading result...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">
            <i className="fa-solid fa-exclamation-triangle"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/ngat-username')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-6xl mb-4">
            <i className="fa-solid fa-file-circle-question"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Result Found</h2>
          <p className="text-gray-600 mb-6">No result data available</p>
          <button
            onClick={() => navigate('/ngat-username')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const passMark = 50; // NGAT pass mark is 50%
  
  // Handle incomplete status
  const isIncomplete = result.total_score === 'incomplete' || result.status === 'Incomplete';
  const percentageScore = isIncomplete ? 'Incomplete' : result.total_score;
  const isPassed = isIncomplete ? false : (percentageScore >= passMark);
  const status = isIncomplete ? 'Incomplete' : (result.status || (isPassed ? 'Pass' : 'Fail'));
  const isComplaintAllowed = Number(result?.complaint_status ?? 0) === 1;
  const isCooldownCooldown = lastSubmissionTime && (Date.now() - lastSubmissionTime) < 30000;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back</span>
          </button>
        </div>

        {/* Results Header */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-xl font-bold flex items-center mb-4 sm:mb-0" style={{ color: 'rgb(18,82,166)' }}>
              <i className="fa-solid fa-graduation-cap mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
              NGAT Result
            </h2>
          </div>
        </section>

        {/* Student Profile Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Side - Student Photo */}
            <div className="flex justify-center lg:justify-start lg:w-80">
              <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-blue-200 shadow-lg">
                {result.photo_url ? (
                    <img 
                      src={`https://ngat.ethernet.edu.et/${result.photo_url}`} 
                      alt={`${result.student_name || 'Student'} Photo`}
                      className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${result.photo_url ? 'hidden' : 'flex'}`}
                >
                  <i className="fa-solid fa-user text-5xl text-gray-600"></i>
                </div>
              </div>
            </div>
            
            {/* Right Side - Student Basic Info Cards */}
            <div className="flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { 
                    icon: 'fa-id-card', 
                    label: 'Full Name', 
                    value: result.student_name || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                  { 
                    icon: 'fa-user', 
                    label: 'Username', 
                    value: result.student_username || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                  { 
                    icon: 'fa-venus-mars', 
                    label: 'Gender', 
                    value: result.gender || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                  { 
                    icon: 'fa-calendar', 
                    label: 'Exam Date', 
                    value: result.exam_date || 'N/A',
                    color: 'text-gray-800',
                    bgColor: 'from-blue-50 to-blue-100'
                  },
                ].map(({ icon, label, value, color, bgColor }) => (
                  <div key={label} className={`p-4 rounded-xl bg-gradient-to-br ${bgColor} border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200`}>
                    <div className="flex items-center mb-2">
                      <i className={`fa-solid ${icon} mr-2 ${color}`} />
                      <p className="text-sm font-medium text-gray-600">{label}</p>
                    </div>
                    <p className={`font-semibold text-lg ${color} break-words`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Results Table */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr style={{ backgroundColor: 'rgb(18,82,166)' }}>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Total Score</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Pass Mark</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Status</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Certificate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {percentageScore}%
                      </div>
                      <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            isIncomplete ? 'bg-yellow-500' : (isPassed ? 'bg-green-600' : 'bg-red-600')
                          }`}
                          style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {passMark}%
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isIncomplete
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : isPassed 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                      {status}
                    </span>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    {isPassed && (
                      <button
                        onClick={downloadCertificate}
                        disabled={isDownloading}
                        className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title="Download Certificate"
                      >
                        <i className={`fa-solid ${isDownloading ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`} />
                        {isDownloading ? 'Downloading...' : 'Download'}
                      </button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-500">Total Score</span>
                    <span className="text-lg font-bold" style={{ color: isIncomplete ? '#d97706' : (isPassed ? '#059669' : '#dc2626') }}>
                      {percentageScore}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        isPassed ? 'bg-green-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-500">Pass Mark</span>
                    <span className="text-lg font-bold text-gray-700">
                      {passMark}%
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Status</span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isIncomplete
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : isPassed 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                      {status}
                    </span>
                  </div>
                </div>

                {isPassed && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Certificate</span>
                      <button
                        onClick={downloadCertificate}
                        disabled={isDownloading}
                        className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title="Download Certificate"
                      >
                        <i className={`fa-solid ${isDownloading ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`} />
                        {isDownloading ? 'Downloading...' : 'Download'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Note Section with Complaint */}
        <section className="mt-8 p-6 sm:p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 shadow-md">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start">
              <i className="fa-solid fa-circle-info text-2xl flex-shrink-0 mt-1" style={{ color: 'rgb(18,82,166)' }} />
              <div className="ml-4">
                <h4 className="text-lg font-semibold mb-2" style={{ color: 'rgb(18,82,166)' }}>Important Note</h4>
                <p className="text-sm mb-2" style={{ color: 'rgb(18,82,166)' }}>
                  This is your official NGAT examination result. For any discrepancies or concerns, please submit a complaint.
                  {!isComplaintAllowed ? ' Complaint period is over.' : ''}
                  {' '}For support,
                  <a
                    href="https://t.me/ngatsupportofficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center ml-1 underline font-medium hover:opacity-80"
                    style={{ color: 'rgb(18,82,166)' }}
                  >
                    <i className="fa-brands fa-telegram mr-1"></i>
                    contact our Telegram channel
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={() => isComplaintAllowed && setShowComplaintModal(true)}
                className={`flex items-center justify-center px-6 py-3 rounded-lg shadow-md transition-colors ${
                  isComplaintAllowed ? 'text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={isComplaintAllowed ? { backgroundColor: 'rgb(18,82,166)' } : undefined}
                onMouseEnter={(e) => {
                  if (isComplaintAllowed) e.currentTarget.style.backgroundColor = 'rgb(15,70,140)';
                }}
                onMouseLeave={(e) => {
                  if (isComplaintAllowed) e.currentTarget.style.backgroundColor = 'rgb(18,82,166)';
                }}
                disabled={!isComplaintAllowed}
              >
                <i className="fa-solid fa-flag mr-2" />
                {isComplaintAllowed ? 'Submit Complaint' : 'Complaint period is over'}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Complaint Modal */}
      {showComplaintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-flag mr-3" style={{ color: 'rgb(18,82,166)' }}></i>
                  Submit Complaint
                </h2>
                <button
                  onClick={handleCancelComplaint}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleComplaintSubmit(); }}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complaint Category *
                  </label>
                  <select
                    value={complaintData.category}
                    onChange={(e) => setComplaintData({ ...complaintData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a category</option>
                    {complaintCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description * ({complaintData.description.length}/255)
                  </label>
                  <textarea
                    value={complaintData.description}
                    onChange={(e) => setComplaintData({ ...complaintData, description: e.target.value })}
                    rows={4}
                    maxLength={255}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      complaintData.description.length > 250 ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Please describe your complaint in detail..."
                    required
                  />
                  {complaintData.description.length > 250 && (
                    <p className="text-sm text-red-600 mt-1">
                      Warning: You're approaching the character limit ({complaintData.description.length}/255)
                    </p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number * (Format: 09*********)
                  </label>
                  <input
                    type="tel"
                    value={complaintData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 10) {
                        setComplaintData({ ...complaintData, phoneNumber: value });
                      }
                    }}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="09*********"
                    required
                  />
                  {complaintData.phoneNumber.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {complaintData.phoneNumber.length}/10 characters
                    </p>
                  )}
                </div>

                {validationError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{validationError}</p>
                  </div>
                )}

                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <i className="fa-solid fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
                    <div>
                      <p className="text-sm text-blue-700 mb-1">
                        If you have multiple complaints, please wait 30 seconds between submissions.
                      </p>
                      <p className="text-xs text-blue-600">
                        You will get a reference number for each complaint individually.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelComplaint}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingComplaint || !isComplaintAllowed || isCooldownCooldown}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingComplaint ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Submitting...
                      </>
                    ) : isCooldownCooldown ? (
                      <>
                        <i className="fa-solid fa-clock mr-2"></i>
                        Please wait...
                      </>
                    ) : (
                      'Submit Complaint'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-check-circle mr-3 text-green-500"></i>
                  Success
                </h2>
                <button
                  onClick={handleCloseSuccessModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-check text-2xl text-green-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Complaint Submitted
                </h3>
                <p className="text-gray-600 mb-6">
                  Your complaint has been submitted successfully. We have received your request and will review it shortly.
                </p>
                <div className="p-4 mb-4 rounded-lg border text-center" style={{
                  backgroundColor: 'rgba(18, 82, 166, 0.05)',
                  borderColor: 'rgba(18, 82, 166, 0.2)'
                }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'rgb(18,82,166)' }}>
                    Save This Reference Number:
                  </p>
                  <div className="flex items-center justify-center mb-3">
                    <span className="font-mono font-bold text-base mr-3" style={{ color: 'rgb(18,82,166)' }}>{complaintReference}</span>
                    <button
                      onClick={copyReferenceNumber}
                      className="px-3 py-1 rounded text-sm font-medium hover:bg-blue-100 transition-colors"
                      style={{ color: 'rgb(18,82,166)' }}
                      title="Copy reference number"
                    >
                      <i className="fa-solid fa-copy mr-1"></i>
                      Copy
                    </button>
                    {copyConfirmed && (
                      <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
                        <i className="fa-solid fa-check mr-1"></i>
                        Copied!
                      </span>
                    )}
                  </div>
                  <p className="text-xs opacity-80" style={{ color: 'rgb(18,82,166)' }}>
                    You can check the complaint result when announced using this reference number.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex justify-center">
                <button
                  onClick={handleCloseSuccessModal}
                  className="px-8 py-3 text-white rounded-lg transition-colors duration-200 font-medium"
                  style={{ backgroundColor: 'rgb(18,82,166)' }}
                >
                  <i className="fa-solid fa-check mr-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NgatResultPage;
